import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMode } from '../../contexts/ModeContext';
import { useLang } from '../../contexts/LangContext';
import {
  BookOpen, Book, Headphones, Target, Calendar,
  ListOrdered, Heart, Quote, Menu, X, LogOut, Settings, Shield, Sparkles, GraduationCap,
  Gamepad2, Users, Star, Globe, Award, Trophy
} from 'lucide-react';
import { authAPI, setAuthToken } from '../../utils/api';
import ParentalConsentModal, { hasParentalConsent } from '../ParentalConsentModal';
import { useNavigate } from 'react-router-dom';

const NAV_ITEMS_ADULT = [
  { to: '/hafizlik', labelTr: 'Hafızlığa Devam', labelEn: 'Continue Hifz', icon: BookOpen, testId: 'nav-continue-hifz' },
  { to: '/oku', labelTr: 'Kuran Oku', labelEn: 'Read Quran', icon: Book, testId: 'nav-read-quran' },
  { to: '/dinle', labelTr: 'Kuran Dinle', labelEn: 'Listen to Quran', icon: Headphones, testId: 'nav-listen-quran' },
  { to: '/hatim', labelTr: 'Hatim Yap', labelEn: 'Complete Hatim', icon: Target, testId: 'nav-hatim' },
  { to: '/challenges', labelTr: 'Grup Hatim', labelEn: 'Group Hatim', icon: Trophy, testId: 'nav-challenges' },
  { to: '/badges', labelTr: 'Rozetlerim', labelEn: 'My Badges', icon: Award, testId: 'nav-badges' },
  { to: '/dini-gunler', labelTr: 'Özel Günler', labelEn: 'Religious Days', icon: Calendar, testId: 'nav-religious-days' },
  { to: '/dualar', labelTr: 'Özel Dualar', labelEn: 'Special Prayers', icon: Heart, testId: 'nav-special-prayers' },
  { to: '/sunnetler', labelTr: 'Sünnetler', labelEn: 'Sunnahs', icon: Star, testId: 'nav-sunnetler' },
  { to: '/hadisler', labelTr: 'Hadisler', labelEn: 'Hadiths', icon: Quote, testId: 'nav-hadiths' },
];

const NAV_ITEMS_KIDS = [
  { to: '/elifba', labelTr: 'Elif-Ba', labelEn: 'Elif-Ba', icon: GraduationCap, testId: 'nav-elifba-kids' },
  { to: '/oku', labelTr: 'Kuran Oku', labelEn: 'Read Quran', icon: Book, testId: 'nav-read-quran' },
  { to: '/hatim', labelTr: 'Hatim Yap', labelEn: 'Complete Hatim', icon: Target, testId: 'nav-hatim' },
  { to: '/challenges', labelTr: 'Grup Hatim', labelEn: 'Group Hatim', icon: Trophy, testId: 'nav-challenges' },
  { to: '/badges', labelTr: 'Rozetlerim', labelEn: 'My Badges', icon: Award, testId: 'nav-badges' },
  { to: '/hafizlik', labelTr: 'Hafızlığa Devam', labelEn: 'Continue Hifz', icon: BookOpen, testId: 'nav-continue-hifz' },
  { to: '/dinle', labelTr: 'Kuran Dinle', labelEn: 'Listen to Quran', icon: Headphones, testId: 'nav-listen-quran' },
  { to: '/alistirmalar', labelTr: 'Eğlenceli Alıştırmalar', labelEn: 'Fun Exercises', icon: Gamepad2, testId: 'nav-games' },
  { to: '/arkadaslar', labelTr: 'Online Arkadaşlar', labelEn: 'Online Friends', icon: Users, testId: 'nav-friends' },
  { to: '/cocuk-hadisleri', labelTr: 'Çocuklara Hadisler', labelEn: 'Hadiths for Children', icon: Quote, testId: 'nav-kid-hadiths' },
  { to: '/sunnetler', labelTr: 'Sünnetler', labelEn: 'Sunnahs', icon: Star, testId: 'nav-sunnetler' },
  { to: '/dualar', labelTr: 'Özel Dualar', labelEn: 'Special Prayers', icon: Heart, testId: 'nav-special-prayers' },
];

export const AppSidebar = ({ user, mobileOpen, onMobileClose }) => {
  const { isKidsMode, toggleMode } = useMode();
  const [showParentalConsent, setShowParentalConsent] = useState(false);
  const handleModeToggle = () => {
    // Switching INTO kids mode → require parental consent first time
    if (!isKidsMode && !hasParentalConsent()) {
      setShowParentalConsent(true);
      return;
    }
    toggleMode();
  };
  const { lang, toggleLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const accent = isKidsMode ? 'amber' : 'teal';
  const isGuest = !user;
  // Guests can only see Read / Listen Quran in the sidebar
  const PUBLIC_PATHS = ['/oku', '/dinle'];
  const fullNav = isKidsMode ? NAV_ITEMS_KIDS : NAV_ITEMS_ADULT;
  const navItems = isGuest ? fullNav.filter((n) => PUBLIC_PATHS.includes(n.to)) : fullNav;

  const handleLogout = () => {
    setAuthToken(null);
    navigate('/');
  };

  const sidebarBg = isKidsMode ? 'bg-amber-50/40' : 'bg-stone-50';
  const activeBg = isKidsMode ? 'bg-amber-500 text-white' : 'bg-teal-700 text-white';
  const hoverBg = isKidsMode ? 'hover:bg-amber-100' : 'hover:bg-stone-200/60';
  const logoColor = isKidsMode ? 'text-amber-700' : 'text-teal-800';

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`
          fixed lg:sticky top-0 left-0 z-50 lg:z-auto
          h-screen w-72 ${sidebarBg} border-r border-stone-200
          flex flex-col transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-stone-200/60 flex items-center justify-between">
          <NavLink to={isGuest ? '/oku' : '/hafizlik'} className="flex items-center gap-3" data-testid="sidebar-logo">
            <div className={`w-10 h-10 rounded-xl ${isKidsMode ? 'bg-amber-500' : 'bg-teal-700'} flex items-center justify-center text-white text-xl font-bold`}>
              ح
            </div>
            <div>
              <div className={`text-xl font-bold ${logoColor}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hafızım
              </div>
              <div className="text-xs text-stone-500">Kuran ile bir yolculuk</div>
            </div>
          </NavLink>

          <button
            type="button"
            onClick={onMobileClose}
            className="lg:hidden p-2 rounded-lg hover:bg-stone-100"
            data-testid="sidebar-close-mobile"
            aria-label="Menüyü kapat"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {navItems.map(({ to, labelTr, labelEn, icon: Icon, testId }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  onClick={onMobileClose}
                  data-testid={testId}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-3 rounded-xl
                    text-sm font-medium transition-colors
                    ${isActive ? activeBg : `text-stone-700 ${hoverBg}`}
                  `}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{lang === 'en' ? labelEn : labelTr}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Secondary section */}
          {!isGuest && (
          <div className="mt-6 pt-4 border-t border-stone-200/60">
            <p className="px-4 mb-2 text-xs uppercase tracking-[0.2em] text-stone-400">{lang === 'en' ? 'Other' : 'Diğer'}</p>
            <ul className="space-y-1">
              {!isKidsMode && (
                <li>
                  <NavLink
                    to="/elifba"
                    onClick={onMobileClose}
                    data-testid="nav-elifba"
                    className={({ isActive }) => `
                      flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                      ${isActive ? activeBg : `text-stone-700 ${hoverBg}`}
                    `}
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-base">ا</span>
                    <span>{lang === 'en' ? 'Elif-Ba (Kids)' : 'Elif-Ba (Çocuk Modu)'}</span>
                  </NavLink>
                </li>
              )}
              <li>
                <NavLink
                  to="/practice"
                  onClick={onMobileClose}
                  data-testid="nav-practice"
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                    ${isActive ? activeBg : `text-stone-700 ${hoverBg}`}
                  `}
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Eski Pratik (Geçici)</span>
                </NavLink>
              </li>
              {user?.is_admin && (
                <li>
                  <NavLink
                    to="/admin"
                    onClick={onMobileClose}
                    data-testid="nav-admin"
                    className={({ isActive }) => `
                      flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                      ${isActive ? activeBg : `text-stone-700 ${hoverBg}`}
                    `}
                  >
                    <Shield className="w-5 h-5" />
                    <span>Admin Panel</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
          )}
        </nav>

        {/* Profile footer */}
        <div className="p-3 border-t border-stone-200/60 space-y-1">
          {/* Language Toggle — hidden (Turkish-only) */}
          {/* Mode Toggle — only for logged in users */}
          {!isGuest && (
          <button
            type="button"
            onClick={handleModeToggle}
            data-testid="sidebar-mode-toggle"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${hoverBg} transition-colors border border-stone-200/80`}
            title={isKidsMode ? 'Yetişkin moduna geç' : 'Çocuk moduna geç'}
          >
            {isKidsMode ? (
              <>
                <GraduationCap className="w-5 h-5 text-amber-600" />
                <span className="flex-1 text-left">{lang === 'en' ? 'Kids Mode' : 'Çocuk Modu'}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{lang === 'en' ? 'On' : 'Aktif'}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 text-teal-700" />
                <span className="flex-1 text-left">{lang === 'en' ? 'Adult Mode' : 'Yetişkin Modu'}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-semibold">{lang === 'en' ? 'On' : 'Aktif'}</span>
              </>
            )}
          </button>
          )}

          {isGuest ? (
            <button
              type="button"
              onClick={() => navigate('/auth')}
              data-testid="sidebar-signin"
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'} transition-colors`}
            >
              <LogOut className="w-5 h-5 rotate-180" />
              <span>Giriş Yap / Kayıt Ol</span>
            </button>
          ) : (
            <>
              <NavLink
                to="/settings"
                onClick={onMobileClose}
                data-testid="nav-settings"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-stone-700 ${hoverBg} transition-colors`}
              >
                <Settings className="w-5 h-5" />
                <span className="truncate flex-1">{user?.name || 'Profil'}</span>
              </NavLink>
              <button
                type="button"
                onClick={handleLogout}
                data-testid="sidebar-logout"
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-stone-600 ${hoverBg} transition-colors`}
              >
                <LogOut className="w-5 h-5" />
                <span>{lang === 'en' ? 'Sign Out' : 'Çıkış Yap'}</span>
              </button>
            </>
          )}
        </div>
      </aside>

      <ParentalConsentModal
        open={showParentalConsent}
        onAccept={() => { setShowParentalConsent(false); toggleMode(); }}
        onCancel={() => setShowParentalConsent(false)}
      />
    </>
  );
};

export const MobileTopbar = ({ onMenuClick, title }) => {
  const { isKidsMode } = useMode();
  return (
    <header
      data-testid="mobile-topbar"
      className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-stone-200 px-4 py-3 flex items-center gap-3"
    >
      <button
        type="button"
        onClick={onMenuClick}
        data-testid="mobile-menu-button"
        className="p-2 rounded-lg hover:bg-stone-100"
        aria-label="Menüyü aç"
      >
        <Menu className="w-5 h-5 text-stone-700" />
      </button>
      <div className={`text-base font-semibold ${isKidsMode ? 'text-amber-700' : 'text-teal-800'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
        {title || 'Hafızım'}
      </div>
    </header>
  );
};

export const AppShell = ({ user, title, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isKidsMode } = useMode();

  return (
    <div
      className={`min-h-screen flex ${isKidsMode ? 'bg-amber-50/30' : 'bg-stone-50'}`}
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="app-shell"
    >
      <AppSidebar
        user={user}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <MobileTopbar onMenuClick={() => setMobileOpen(true)} title={title} />
        <main className="flex-1 overflow-y-auto" data-testid="app-main">
          {children}
        </main>
        <LegalFooter />
      </div>
    </div>
  );
};

const LegalFooter = () => (
  <footer className="border-t border-stone-200 bg-white/60 backdrop-blur-sm px-4 sm:px-8 py-3 text-[11px] text-stone-500" data-testid="legal-footer">
    <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-2">
      <span>© {new Date().getFullYear()} Hafızım</span>
      <nav className="flex flex-wrap items-center gap-3">
        <a href="/gizlilik-politikasi" className="hover:text-stone-800 hover:underline" data-testid="footer-privacy">Gizlilik & KVKK</a>
        <a href="/kullanim-kosullari" className="hover:text-stone-800 hover:underline" data-testid="footer-terms">Kullanım Koşulları</a>
        <a href="/cerez-politikasi" className="hover:text-stone-800 hover:underline" data-testid="footer-cookies">Çerez Politikası</a>
        <a href="/iletisim" className="hover:text-stone-800 hover:underline" data-testid="footer-contact">İletişim</a>
      </nav>
    </div>
  </footer>
);

export default AppShell;
